<template>
  <div class="admin-surveys-page">
    <el-card class="box-card" shadow="never">
      <!-- HEADER -->
      <div slot="header" class="card-header">
        <div>
          <h2 class="page-title">Модуль опитувань споживачів (НКРЕКП)</h2>
          <span class="page-subtitle">Керування опитуваннями, імпорт структури з Excel (.xlsx) та налаштування періодів</span>
        </div>
        <div class="header-actions">
          <el-button
            type="info"
            size="small"
            plain
            icon="el-icon-download"
            @click="handleDownloadTemplate"
          >
            Завантажити шаблон Excel
          </el-button>
          <el-button
            type="warning"
            size="small"
            plain
            icon="el-icon-magic-stick"
            :loading="isLoading"
            @click="handleSeedSamples"
          >
            Ініціалізувати зразки НКРЕКП
          </el-button>
          <el-button
            type="success"
            size="small"
            icon="el-icon-user"
            @click="openConsumerImportDialog"
          >
            Імпортувати споживачів (Excel)
          </el-button>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-upload2"
            @click="openImportDialog"
          >
            Імпортувати опитування з Excel
          </el-button>
          <el-button
            type="info"
            size="small"
            plain
            icon="el-icon-link"
            @click="openIntegrationLinksDialog"
          >
            Посилання для переходу з сайту
          </el-button>
        </div>
      </div>

      <!-- CONSUMER & SURVEY STATS STRIP -->
      <div class="stats-strip">
        <div class="stat-card">
          <div class="stat-icon-wrap bg-blue">
            <i class="el-icon-user"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ totalConsumersCount.toLocaleString() }}</div>
            <div class="stat-label">Всього споживачів у базі</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap bg-green">
            <i class="el-icon-house"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ (consumerStats.household || 0).toLocaleString() }}</div>
            <div class="stat-label">Побутових споживачів</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap bg-orange">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ (consumerStats.nonHousehold || 0).toLocaleString() }}</div>
            <div class="stat-label">Непобутових (юр. осіб)</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon-wrap bg-purple">
            <i class="el-icon-circle-check"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ (consumerStats.votedCount || 0).toLocaleString() }}</div>
            <div class="stat-label">Взяли участь в опитуваннях</div>
          </div>
        </div>
      </div>

      <!-- SURVEYS TABLE -->
      <el-table
        :data="surveysList"
        v-loading="isLoading"
        stripe
        style="width: 100%"
        class="surveys-table"
      >
        <el-table-column prop="title" label="Назва опитування" min-width="260">
          <template slot-scope="scope">
            <div class="survey-title-cell">
              <strong>{{ scope.row.title }}</strong>
              <div class="survey-meta-sub">
                Блоків: {{ (scope.row.blocks && scope.row.blocks.length) || 0 }},
                Запитань: {{ countQuestions(scope.row) }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Тип споживача" width="170">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.consumerType === 'household' ? 'success' : (scope.row.consumerType === 'non_household' ? 'warning' : 'info')"
              size="small"
            >
              {{ scope.row.consumerType === 'household' ? 'Побутові' : (scope.row.consumerType === 'non_household' ? 'Непобутові' : 'Всі') }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Період проведення" width="220">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row.startDate) }} – {{ formatDate(scope.row.endDate) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Статус" width="170">
          <template slot-scope="scope">
            <div class="status-cell-wrap">
              <el-tag
                :type="computeStatusTag(scope.row)"
                size="small"
                effect="dark"
              >
                {{ computeStatusName(scope.row) }}
              </el-tag>
              <div v-if="isPeriodExpired(scope.row)" class="period-expired-sub">
                <i class="el-icon-time"></i> Термін минув
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="responseCount" label="Відповідей" width="130" align="center">
          <template slot-scope="scope">
            <el-button
              size="mini"
              :type="(scope.row.responseCount > 0) ? 'primary' : 'info'"
              plain
              round
              icon="el-icon-tickets"
              class="response-count-pill"
              @click="goToResults(scope.row._id)"
            >
              {{ scope.row.responseCount || 0 }} анкет
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="Дії" width="370" align="right">
          <template slot-scope="scope">
            <!-- 1-Click Manual Deactivate / Activate Button -->
            <el-button
              v-if="scope.row.status === 'ACTIVE'"
              size="mini"
              type="danger"
              plain
              icon="el-icon-video-pause"
              class="status-toggle-btn"
              @click="toggleSurveyStatus(scope.row)"
            >
              Деактивувати
            </el-button>
            <el-button
              v-else
              size="mini"
              type="success"
              plain
              icon="el-icon-video-play"
              class="status-toggle-btn"
              @click="toggleSurveyStatus(scope.row)"
            >
              Активувати
            </el-button>

            <el-button
              size="mini"
              type="text"
              icon="el-icon-view"
              @click="previewSurvey(scope.row._id)"
            >
              Перегляд
            </el-button>
            <el-button
              size="mini"
              type="text"
              icon="el-icon-edit"
              @click="openEditDialog(scope.row)"
            >
              Редагувати
            </el-button>
            <el-button
              size="mini"
              type="text"
              icon="el-icon-data-analysis"
              @click="goToResults(scope.row._id)"
            >
              Результати
            </el-button>
            <el-button
              size="mini"
              type="text"
              icon="el-icon-delete"
              style="color: #f56c6c;"
              @click="confirmDeleteSurvey(scope.row)"
            >
              Видалити
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- EMPTY STATE -->
      <div v-if="!isLoading && (!surveysList || surveysList.length === 0)" class="empty-state">
        <i class="el-icon-document empty-icon"></i>
        <p>Не знайдено жодного опитування.</p>
        <el-button type="primary" size="small" @click="handleSeedSamples">
          Створити стандартні опитування НКРЕКП
        </el-button>
      </div>
    </el-card>

    <!-- IMPORT DIALOG -->
    <el-dialog
      title="Імпорт структури опитування з Excel (.xlsx)"
      :visible.sync="importDialogVisible"
      width="580px"
      :close-on-click-modal="false"
    >
      <el-form :model="importForm" label-position="top" size="small">
        <el-form-item label="Назва опитування (залиште порожнім для автовизначення)">
          <el-input v-model="importForm.title" placeholder="Наприклад: Опитування побутових споживачів 2026" />
        </el-form-item>

        <el-form-item label="Тип споживача">
          <el-select v-model="importForm.consumerType" style="width: 100%">
            <el-option label="Побутовий споживач (населення)" value="household" />
            <el-option label="Непобутовий споживач (комерційні / юридичні особи)" value="non_household" />
          </el-select>
        </el-form-item>

        <el-form-item label="Період проведення">
          <el-date-picker
            v-model="importForm.dateRange"
            type="daterange"
            range-separator="до"
            start-placeholder="Дата початку"
            end-placeholder="Дата завершення"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="Файл опитування Microsoft Excel (.xlsx)">
          <el-upload
            class="upload-box"
            drag
            action=""
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            accept=".xlsx, .xls"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">Перетягніть файл сюди або <em>натисніть для вибору</em></div>
            <div class="el-upload__tip" slot="tip">Файл формату .xlsx згідно з погодженим шаблоном НКРЕКП</div>
          </el-upload>
        </el-form-item>

        <div v-if="importErrors.length > 0" class="import-errors-box">
          <el-alert
            title="Помилки валідації Excel-файлу:"
            type="error"
            :closable="false"
            show-icon
          >
            <ul>
              <li v-for="(err, i) in importErrors" :key="i">{{ err }}</li>
            </ul>
          </el-alert>
        </div>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="importDialogVisible = false">Скасувати</el-button>
        <el-button
          size="small"
          type="primary"
          :loading="importing"
          :disabled="!selectedFile"
          @click="submitImport"
        >
          <i class="el-icon-upload2"></i> Імпортувати опитування
        </el-button>
      </span>
    </el-dialog>

    <!-- IMPORT CONSUMERS MODAL -->
    <el-dialog
      title="Імпорт реєстру споживачів з Excel"
      :visible.sync="consumerImportDialogVisible"
      width="640px"
      :close-on-click-modal="false"
    >
      <el-form label-position="top" size="small">
        <el-alert
          title="Підтримуються дві вигрузки споживачів від клієнта:"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 15px;"
        >
          <div style="font-size: 13px; line-height: 1.6; margin-top: 6px;">
            <div><strong>1. Непобутові (юр. особи):</strong> Договір | EIC-код | Назва організації | Фактичний об'єм</div>
            <div><strong>2. Побутові (населення):</strong> Особ.рах. | EIC код | Група споживача | Фактичний об'єм | Ліч. | Насел.пункт тип</div>
          </div>
        </el-alert>

        <el-form-item label="Категорія споживачів для імпорту">
          <el-radio-group v-model="consumerImportType" size="small">
            <el-radio-button label="auto">Авто-визначення за структурою</el-radio-button>
            <el-radio-button label="household">Побутові (населення)</el-radio-button>
            <el-radio-button label="non_household">Непобутові (юридичні особи)</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="Виберіть файл Excel (.xlsx або .xls)">
          <el-upload
            class="upload-box"
            drag
            action=""
            :auto-upload="false"
            :limit="1"
            :on-change="handleConsumerFileChange"
            :on-remove="handleConsumerFileRemove"
            accept=".xlsx, .xls"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">Перетягніть файл сюди або <em>натисніть для вибору</em></div>
            <div class="el-upload__tip" slot="tip">Таблиця Excel із колонками EIC-кодів, особових рахунків/договорів та обсягів споживання</div>
          </el-upload>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="consumerImportDialogVisible = false">Скасувати</el-button>
        <el-button
          size="small"
          type="success"
          :loading="consumerImporting"
          :disabled="!selectedConsumerFile"
          @click="submitConsumerImport"
        >
          <i class="el-icon-upload2"></i> Завантажити споживачів
        </el-button>
      </span>
    </el-dialog>

    <!-- EDIT SURVEY MODAL -->
    <el-dialog
      title="Редагування параметрів опитування"
      :visible.sync="editDialogVisible"
      width="520px"
    >
      <el-form :model="editForm" label-position="top" size="small">
        <el-form-item label="Назва опитування">
          <el-input v-model="editForm.title" />
        </el-form-item>

        <el-form-item label="Статус опитування">
          <el-select v-model="editForm.status" style="width: 100%">
            <el-option label="Активне (приймає відповіді)" value="ACTIVE" />
            <el-option label="Деактивовано (прийом відповідей закрито)" value="COMPLETED" />
            <el-option label="Чернетка" value="DRAFT" />
            <el-option label="Заплановано" value="SCHEDULED" />
          </el-select>
        </el-form-item>

        <el-form-item label="Період проведення">
          <el-date-picker
            v-model="editForm.dateRange"
            type="daterange"
            range-separator="до"
            start-placeholder="Дата початку"
            end-placeholder="Дата завершення"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="Інформаційне повідомлення НКРЕКП">
          <el-input type="textarea" :rows="3" v-model="editForm.description" />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="editDialogVisible = false">Скасувати</el-button>
        <el-button size="small" type="primary" :loading="isUpdating" @click="saveSurveyEdit">
          Зберегти зміни
        </el-button>
      </span>
    </el-dialog>

    <!-- INTEGRATION LINKS MODAL -->
    <el-dialog
      title="Посилання для переходу споживачів з сайту / кабінету"
      :visible.sync="integrationLinksDialogVisible"
      width="680px"
    >
      <el-alert
        title="Автоматична ідентифікація побутових споживачів"
        type="success"
        description="Для побутових споживачів достатньо передати номер особового рахунку в URL параметрі (accountNumber або account). Система автоматично підтягне EIC-код точки обліку та ідентифікує споживача без необхідності вводити 16-значний код вручну!"
        show-icon
        :closable="false"
        style="margin-bottom: 20px;"
      />

      <div class="integration-links-list">
        <div class="link-item-box">
          <div class="link-item-header">
            <strong>1. Пряме посилання для ПОБУТОВИХ споживачів (без параметрів):</strong>
            <el-tag size="mini" type="success" style="margin-left: 8px;">Населення</el-tag>
          </div>
          <p class="link-item-desc" style="font-size: 13px; color: #475569; margin: 6px 0 10px 0;">
            Прямий перехід на форму опитування для населення (споживач сам вводить особовий рахунок або EIC-код):
          </p>
          <div class="url-copy-row" style="display: flex; gap: 8px;">
            <el-input :value="householdDirectUrl" readonly size="small">
              <template slot="prepend">URL:</template>
            </el-input>
            <el-button size="small" type="primary" icon="el-icon-document-copy" @click="copyLink(householdDirectUrl)">
              Скопіювати
            </el-button>
          </div>
        </div>

        <div class="link-item-box" style="margin-top: 18px; padding-top: 15px; border-top: 1px dashed #e2e8f0;">
          <div class="link-item-header">
            <strong>2. Пряме посилання для НЕПОБУТОВИХ споживачів (без параметрів):</strong>
            <el-tag size="mini" type="warning" style="margin-left: 8px;">Юр. особи / ФОП</el-tag>
          </div>
          <p class="link-item-desc" style="font-size: 13px; color: #475569; margin: 6px 0 10px 0;">
            Прямий перехід на форму опитування для підприємств та бізнесу (введення EIC-коду):
          </p>
          <div class="url-copy-row" style="display: flex; gap: 8px;">
            <el-input :value="nonHouseholdDirectUrl" readonly size="small">
              <template slot="prepend">URL:</template>
            </el-input>
            <el-button size="small" type="warning" icon="el-icon-document-copy" @click="copyLink(nonHouseholdDirectUrl)">
              Скопіювати
            </el-button>
          </div>
        </div>

        <div class="link-item-box" style="margin-top: 18px; padding-top: 15px; border-top: 1px dashed #e2e8f0;">
          <div class="link-item-header">
            <strong>3. Автоматичний перехід з сайту для побутових (з номером рахунку в URL):</strong>
            <el-tag size="mini" type="info" style="margin-left: 8px;">Автопідтягування</el-tag>
          </div>
          <p class="link-item-desc" style="font-size: 13px; color: #475569; margin: 6px 0 10px 0;">
            Для кнопок або банерів на сайті, коли номер особового рахунку вже відомий:
          </p>
          <div class="url-copy-row" style="display: flex; gap: 8px;">
            <el-input :value="householdAccountUrlTemplate" readonly size="small">
              <template slot="prepend">URL:</template>
            </el-input>
            <el-button size="small" icon="el-icon-document-copy" @click="copyLink(householdAccountUrlTemplate)">
              Скопіювати
            </el-button>
          </div>
          <div class="url-example-hint" style="font-size: 12px; color: #64748b; margin-top: 6px;">
            Приклад посилання: <code style="color: #0284c7; background: #f1f5f9; padding: 2px 6px; border-radius: 4px;">{{ householdAccountUrlExample }}</code>
          </div>
        </div>

        <div class="link-item-box" style="margin-top: 18px; padding-top: 15px; border-top: 1px dashed #e2e8f0;">
          <div class="link-item-header">
            <strong>4. Загальна публічна сторінка входу в опитування:</strong>
          </div>
          <p class="link-item-desc" style="font-size: 13px; color: #475569; margin: 6px 0 10px 0;">
            Універсальна сторінка з вибором між побутовим та непобутовим споживачем:
          </p>
          <div class="url-copy-row" style="display: flex; gap: 8px;">
            <el-input :value="publicEntryUrl" readonly size="small">
              <template slot="prepend">URL:</template>
            </el-input>
            <el-button size="small" icon="el-icon-document-copy" @click="copyLink(publicEntryUrl)">
              Скопіювати
            </el-button>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="integrationLinksDialogVisible = false">Закрити</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import axios from 'axios';

export default {
  name: 'AdminSurveys',
  data() {
    return {
      importDialogVisible: false,
      editDialogVisible: false,
      selectedFile: null,
      importing: false,
      isUpdating: false,
      importErrors: [],
      importForm: {
        title: '',
        consumerType: 'household',
        dateRange: [new Date('2026-10-01'), new Date('2026-12-30')],
      },
      editForm: {
        id: '',
        title: '',
        status: 'ACTIVE',
        dateRange: [],
        description: '',
      },
      consumerImportDialogVisible: false,
      selectedConsumerFile: null,
      consumerImporting: false,
      consumerImportType: 'auto',
      consumerStats: {
        total: 0,
        household: 0,
        nonHousehold: 0,
        votedCount: 0,
      },
      integrationLinksDialogVisible: false,
    };
  },
  computed: {
    ...mapGetters('Survey', ['surveysList', 'isLoading']),
    origin() {
      return window.location.origin;
    },
    householdAccountUrlTemplate() {
      return `${this.origin}/survey/household?accountNumber={НОМЕР_РАХУНКУ}`;
    },
    householdAccountUrlExample() {
      return `${this.origin}/survey/household?accountNumber=12004567`;
    },
    eicUrlTemplate() {
      return `${this.origin}/survey/entry?eic={EIC_КОД}`;
    },
    publicEntryUrl() {
      return `${this.origin}/survey/entry`;
    },
    householdDirectUrl() {
      return `${this.origin}/survey/household`;
    },
    nonHouseholdDirectUrl() {
      return `${this.origin}/survey/non-household`;
    },
    totalConsumersCount() {
      return (this.consumerStats.household || 0) + (this.consumerStats.nonHousehold || 0);
    },
  },
  created() {
    this.fetchAdminSurveys();
    this.loadConsumerStats();
  },
  methods: {
    ...mapActions('Survey', [
      'fetchAdminSurveys',
      'importSurveyExcel',
      'updateSurveyStatus',
      'seedSampleSurveys',
      'deleteSurvey',
      'importConsumersExcel',
      'fetchConsumersStats',
    ]),

    async loadConsumerStats() {
      try {
        const stats = await this.fetchConsumersStats();
        if (stats) {
          this.consumerStats = stats;
        }
      } catch (e) {
        console.error('Error loading consumer stats:', e);
      }
    },

    openConsumerImportDialog() {
      this.selectedConsumerFile = null;
      this.consumerImportType = 'auto';
      this.consumerImportDialogVisible = true;
    },

    handleConsumerFileChange(file) {
      this.selectedConsumerFile = file.raw;
    },

    handleConsumerFileRemove() {
      this.selectedConsumerFile = null;
    },

    async submitConsumerImport() {
      if (!this.selectedConsumerFile) return;

      this.consumerImporting = true;
      const formData = new FormData();
      formData.append('file', this.selectedConsumerFile);
      formData.append('consumerType', this.consumerImportType);

      try {
        const res = await this.importConsumersExcel(formData);
        this.$message({
          type: 'success',
          message: res.message || 'Споживачів успішно імпортовано!',
          duration: 5000,
        });
        this.consumerImportDialogVisible = false;
        this.selectedConsumerFile = null;
        await this.loadConsumerStats();
      } catch (err) {
        const errMsg = (err.response && err.response.data && err.response.data.error) || 'Помилка завантаження споживачів';
        this.$message.error(errMsg);
      } finally {
        this.consumerImporting = false;
      }
    },

    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD.MM.YYYY');
    },

    countQuestions(survey) {
      if (!survey.blocks) return 0;
      return survey.blocks.reduce((acc, b) => acc + ((b.questions && b.questions.length) || 0), 0);
    },

    isPeriodExpired(survey) {
      if (!survey || !survey.endDate) return false;
      return new Date(survey.endDate) < new Date();
    },

    computeStatusTag(survey) {
      if (!survey) return 'info';
      if (survey.status === 'COMPLETED') return 'info';
      if (survey.status === 'DRAFT') return 'warning';
      if (this.isPeriodExpired(survey)) return 'danger';
      if (survey.status === 'ACTIVE') return 'success';
      if (survey.status === 'SCHEDULED') return 'primary';
      return 'info';
    },

    computeStatusName(survey) {
      if (!survey) return '';
      if (survey.status === 'COMPLETED') return 'Деактивовано';
      if (survey.status === 'DRAFT') return 'Чернетка';
      if (this.isPeriodExpired(survey)) return 'Період минув';
      if (survey.startDate && new Date(survey.startDate) > new Date()) return 'Заплановано';
      if (survey.status === 'ACTIVE') return 'Активне';
      return survey.status;
    },

    async toggleSurveyStatus(survey) {
      const willActivate = survey.status !== 'ACTIVE';
      const title = survey.title || 'Опитування';
      const confirmText = willActivate
        ? `Ви дійсно бажаєте активувати опитування "${title}"? Після активації споживачі зможуть проходити це опитування.`
        : `Ви дійсно бажаєте деактивувати опитування "${title}"? Після деактивації прийом відповідей буде зупинено, а банер в кабінеті споживачів буде вимкнено.`;

      try {
        await this.$confirm(confirmText, willActivate ? 'Активація опитування' : 'Деактивація опитування', {
          confirmButtonText: willActivate ? 'Активувати' : 'Деактивувати',
          cancelButtonText: 'Скасувати',
          type: willActivate ? 'success' : 'warning',
          confirmButtonClass: willActivate ? 'el-button--success' : 'el-button--danger',
        });

        const newStatus = willActivate ? 'ACTIVE' : 'COMPLETED';
        await this.updateSurveyStatus({
          id: survey._id,
          data: { status: newStatus },
        });

        this.$message.success(`Опитування успішно ${willActivate ? 'активовано' : 'деактивовано'}`);
        await this.fetchAdminSurveys();
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error('Помилка зміни статусу опитування');
        }
      }
    },

    openImportDialog() {
      this.selectedFile = null;
      this.importErrors = [];
      this.importForm.title = '';
      this.importForm.consumerType = 'household';
      this.importForm.dateRange = [new Date('2026-10-01'), new Date('2026-12-30')];
      this.importDialogVisible = true;
    },

    handleFileChange(file) {
      this.selectedFile = file.raw;
      this.importErrors = [];
    },

    handleFileRemove() {
      this.selectedFile = null;
      this.importErrors = [];
    },

    async submitImport() {
      if (!this.selectedFile) {
        this.$message.warning('Будь ласка, оберіть файл Excel для імпорту');
        return;
      }

      this.importing = true;
      this.importErrors = [];

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.importForm.title) formData.append('title', this.importForm.title);
        formData.append('consumerType', this.importForm.consumerType);
        if (this.importForm.dateRange && this.importForm.dateRange.length === 2) {
          formData.append('startDate', this.importForm.dateRange[0].toISOString());
          formData.append('endDate', this.importForm.dateRange[1].toISOString());
        }

        const result = await this.importSurveyExcel(formData);
        this.$message.success(result.message || 'Опитування успішно імпортовано');
        this.importDialogVisible = false;
      } catch (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          this.importErrors = err.response.data.errors;
        } else {
          this.$message.error((err.response && err.response.data && err.response.data.error) || 'Помилка імпорту');
        }
      } finally {
        this.importing = false;
      }
    },

    openEditDialog(survey) {
      this.editForm = {
        id: survey._id,
        title: survey.title,
        status: survey.status,
        dateRange: [new Date(survey.startDate), new Date(survey.endDate)],
        description: survey.description,
      };
      this.editDialogVisible = true;
    },

    async saveSurveyEdit() {
      this.isUpdating = true;
      try {
        const data = {
          title: this.editForm.title,
          status: this.editForm.status,
          description: this.editForm.description,
        };
        if (this.editForm.dateRange && this.editForm.dateRange.length === 2) {
          data.startDate = this.editForm.dateRange[0];
          data.endDate = this.editForm.dateRange[1];
        }

        await this.updateSurveyStatus({
          id: this.editForm.id,
          data,
        });

        this.$message.success('Параметри опитування успішно оновлено');
        this.editDialogVisible = false;
      } catch (err) {
        this.$message.error('Помилка оновлення опитування');
      } finally {
        this.isUpdating = false;
      }
    },

    previewSurvey(id) {
      this.$router.push({ name: 'survey-form', params: { id } });
    },

    goToResults(surveyId) {
      this.$router.push({
        name: 'admin-survey-results',
        query: { surveyId },
      });
    },

    async handleDownloadTemplate() {
      try {
        this.$message.info('Завантаження шаблону...');
        const response = await axios.get('/api/survey/admin/template', {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Survey_Template_NKREKP.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('Шаблон успішно завантажено');
      } catch (e) {
        this.$message.error('Не вдалося завантажити шаблон Excel');
      }
    },

    async handleSeedSamples() {
      try {
        await this.$confirm(
          'Створити зразкові опитування НКРЕКП 2026 (для побутових та непобутових споживачів) та тестові дані?',
          'Ініціалізація зразків',
          {
            confirmButtonText: 'Створити',
            cancelButtonText: 'Скасувати',
            type: 'info',
          }
        );

        const res = await this.seedSampleSurveys();
        this.$message.success(res.message || 'Зразкові опитування створено');
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('Помилка ініціалізації');
        }
      }
    },

    async confirmDeleteSurvey(survey) {
      try {
        await this.$confirm(
          `Ви дійсно бажаєте видалити опитування "${survey.title}" та всі пов'язані відповіді (${survey.responseCount || 0} анкет)? Цю дію неможливо скасувати.`,
          'Підтвердження видалення',
          {
            confirmButtonText: 'Видалити',
            cancelButtonText: 'Скасувати',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        );

        await this.deleteSurvey(survey._id);
        this.$message.success('Опитування успішно видалено');
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error((e.response && e.response.data && e.response.data.error) || 'Помилка видалення опитування');
        }
      }
    },

    openIntegrationLinksDialog() {
      this.integrationLinksDialogVisible = true;
    },

    copyLink(text) {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('Посилання скопійовано в буфер обміну!');
        }).catch(() => {
          this.$message.info(text);
        });
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.$message.success('Посилання скопійовано в буфер обміну!');
        } catch (e) {
          this.$message.info(text);
        }
        document.body.removeChild(textArea);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-surveys-page {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .page-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
  }

  .page-subtitle {
    font-size: 13px;
    color: #64748b;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
}

.surveys-table {
  margin-top: 10px;

  .survey-title-cell {
    strong {
      color: #1e293b;
      font-size: 14px;
    }
    .survey-meta-sub {
      font-size: 12px;
      color: #64748b;
      margin-top: 2px;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 50px 20px;

  .empty-icon {
    font-size: 48px;
    color: #94a3b8;
    margin-bottom: 12px;
  }

  p {
    font-size: 15px;
    color: #64748b;
    margin-bottom: 16px;
  }
}

.upload-box {
  width: 100%;
  ::v-deep .el-upload {
    width: 100%;
    .el-upload-dragger {
      width: 100%;
    }
  }
}

.import-errors-box {
  margin-top: 16px;
  ul {
    margin: 6px 0 0 0;
    padding-left: 18px;
    li {
      font-size: 12px;
      line-height: 1.5;
    }
  }
}

.response-count-pill {
  font-weight: 600;
  font-size: 12px;
  padding: 6px 12px;
}

.status-cell-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;

  .period-expired-sub {
    font-size: 11px;
    color: #e53e3e;
    font-weight: 600;
  }
}

.status-toggle-btn {
  font-weight: 600;
  margin-right: 6px;
}

.stats-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin: 15px 0 25px 0;

  .stat-card {
    display: flex;
    align-items: center;
    padding: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    .stat-icon-wrap {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 14px;
      font-size: 24px;

      &.bg-blue {
        background-color: #ebf8ff;
        color: #3182ce;
      }
      &.bg-green {
        background-color: #f0fff4;
        color: #38a169;
      }
      &.bg-orange {
        background-color: #fffaf0;
        color: #dd6b20;
      }
      &.bg-purple {
        background-color: #faf5ff;
        color: #805ad5;
      }
    }

    .stat-info {
      .stat-value {
        font-size: 20px;
        font-weight: 700;
        color: #1a202c;
        line-height: 1.2;
      }
      .stat-label {
        font-size: 12px;
        color: #718096;
        margin-top: 3px;
      }
    }
  }
}
</style>
